import { Fragment, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface ReturnNote {
  id: number;
  note: string;
  action_type: string;
  created_at: string;
}

interface ReturnRow {
  id: number;
  date: string;
  order_id: string;
  sku: string;
  asin: string;
  title: string;
  quantity: number;
  reason: string;
  disposition: string;
  status: string;
  fulfillment_center: string;
  customer_comments: string;
  marketplace: string;
  notes: ReturnNote[];
}

interface ReturnSummary {
  total_returns: number;
  total_entries: number;
  total_order_units: number;
  return_rate: number;
  top_reasons: { reason: string; qty: number; cnt: number }[];
  by_disposition: { disposition: string; qty: number }[];
  by_product: { asin: string; sku: string; title: string; return_units: number; return_count: number; order_units: number; sellable: number; unsellable: number }[];
  by_month?: { month: string; returns: number; qty: number; sellable: number; unsellable: number }[];
}

interface CatalogProduct { asin: string; sku: string; title: string }
interface ProductGroup { label: string; asins: string[]; products: CatalogProduct[] }

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL', A2NODRKZP88ZB9: 'SE',
};

const SC_DOMAINS: Record<string, string> = {
  ATVPDKIKX0DER: 'sellercentral.amazon.com',
  A2EUQ1WTGCTBG2: 'sellercentral.amazon.ca',
  A1AM78C64UM0Y8: 'sellercentral.amazon.com.mx',
  A1F83G8C2ARO7P: 'sellercentral.amazon.co.uk',
  A1PA6795UKMFR9: 'sellercentral.amazon.de',
  A13V1IB3VIYZZH: 'sellercentral.amazon.fr',
  APJ6JRA9NG5V4: 'sellercentral.amazon.it',
  A1RKKUPIHCS9HS: 'sellercentral.amazon.es',
  A1805IZSGTT6HS: 'sellercentral.amazon.nl',
  A2NODRKZP88ZB9: 'sellercentral.amazon.se',
};

function scOrderUrl(marketplace: string, orderId: string) {
  const domain = SC_DOMAINS[marketplace] ?? 'sellercentral.amazon.com';
  return `https://${domain}/orders-v3/order/${orderId}`;
}

const ACTION_TYPES = [
  { value: 'note', label: 'Note', icon: '📝' },
  { value: 'refund_requested', label: 'Refund Requested', icon: '💰' },
  { value: 'replacement_sent', label: 'Replacement Sent', icon: '📦' },
  { value: 'contacted_buyer', label: 'Contacted Buyer', icon: '✉️' },
  { value: 'resolved', label: 'Resolved', icon: '✅' },
];

const ACTION_COLORS: Record<string, string> = {
  note: 'bg-gray-100 text-gray-700',
  refund_requested: 'bg-amber-100 text-amber-700',
  replacement_sent: 'bg-blue-100 text-blue-700',
  contacted_buyer: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
};

function groupProducts(products: CatalogProduct[]): ProductGroup[] {
  const seen = new Set<string>();
  const filtered = products.filter(p => {
    if (!p.title?.trim() || seen.has(p.asin)) return false;
    seen.add(p.asin);
    return true;
  });
  const groups = new Map<string, CatalogProduct[]>();
  for (const p of filtered) {
    const label = p.title.includes('Walkie') ? 'Walkie Talkies'
      : p.title.includes('CarPlay') || p.title.includes('Android') ? 'CarPlay Adapter'
      : p.title.slice(0, 30);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(p);
  }
  return [...groups.entries()].map(([label, prods]) => ({
    label, asins: [...new Set(prods.map(p => p.asin))], products: prods,
  }));
}

export function SalesReturns() {
  const [returns, setReturns] = useState<ReturnRow[]>([]);
  const [summary, setSummary] = useState<ReturnSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const { displayName, parentNickname } = useNicknames();
  const [tab, setTab] = useState<'orders' | 'insights'>('insights');
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [marketplace, setMarketplace] = useState('');
  const [activeDays, setActiveDays] = useState<number | null>(30);
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedAsin, setSelectedAsin] = useState('');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteAction, setNoteAction] = useState('note');

  // Load available marketplaces + product catalog once
  useEffect(() => {
    api.post<{ rows: Array<{ marketplace: string }> }>(
      '/admin/query',
      { sql: "SELECT DISTINCT marketplace FROM amazon_returns_daily WHERE marketplace IS NOT NULL ORDER BY marketplace" },
    ).then(r => setAvailableMarkets(r.rows.map(row => row.marketplace))).catch(() => {});

    api.post<{ rows: CatalogProduct[] }>(
      '/admin/query',
      { sql: "SELECT c.asin, c.sku, c.title FROM amazon_catalog c WHERE c.asin IS NOT NULL AND c.title IS NOT NULL AND c.title != '' AND c.sku NOT LIKE 'amzn.gr.%' GROUP BY c.asin ORDER BY c.title" },
    ).then(r => setProductGroups(groupProducts(r.rows))).catch(() => {});
  }, []);

  const activeGroup = productGroups.find(g => g.label === selectedGroup);
  const effectiveAsin = selectedAsin || (activeGroup ? activeGroup.asins.join(',') : '');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ start, end });
    if (marketplace) params.set('marketplace', marketplace);
    if (effectiveAsin) params.set('asin', effectiveAsin);
    Promise.all([
      api.get<{ returns: ReturnRow[] }>(`/sales/returns?${params}`),
      api.get<ReturnSummary>(`/sales/returns/summary?${params}`),
    ]).then(([r, s]) => {
      setReturns(r.returns);
      setSummary(s);
    }).finally(() => setLoading(false));
  }, [start, end, marketplace, effectiveAsin]);

  useEffect(() => { load(); }, [load]);

  const addNote = async (returnId: number) => {
    if (!noteText.trim()) return;
    const res = await api.post<ReturnNote>(`/sales/returns/${returnId}/notes`, { note: noteText, action_type: noteAction });
    setReturns(prev => prev.map(r =>
      r.id === returnId ? { ...r, notes: [res, ...(r.notes ?? [])] } : r
    ));
    setNoteText('');
    setNoteAction('note');
  };

  const deleteNote = async (returnId: number, noteId: number) => {
    await api.delete(`/sales/returns/notes/${noteId}`);
    setReturns(prev => prev.map(r =>
      r.id === returnId ? { ...r, notes: (r.notes ?? []).filter(n => n.id !== noteId) } : r
    ));
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">From</label>
          <input type="date" value={start} onChange={e => { setStart(e.target.value); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">To</label>
          <input type="date" value={end} onChange={e => { setEnd(e.target.value); setActiveDays(null); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div className="flex gap-2">
          {[
            { key: 'today', label: 'Today', days: 0 },
            { key: 'yesterday', label: 'Yesterday', days: -1 },
            { key: '7d', label: '7d', days: 7 },
            { key: '14d', label: '14d', days: 14 },
            { key: '30d', label: '30d', days: 30 },
            { key: '90d', label: '90d', days: 90 },
          ].map(({ key, label, days }) => (
            <button key={key} onClick={() => {
              const today = new Date();
              if (days === 0) {
                const d = today.toISOString().slice(0, 10);
                setStart(d); setEnd(d);
              } else if (days === -1) {
                const y = new Date(today); y.setDate(y.getDate() - 1);
                const d = y.toISOString().slice(0, 10);
                setStart(d); setEnd(d);
              } else {
                const s = new Date(); s.setDate(s.getDate() - days);
                setStart(s.toISOString().slice(0, 10)); setEnd(today.toISOString().slice(0, 10));
              }
              setActiveDays(days);
            }} className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeDays === days
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
              {label}
            </button>
          ))}
        </div>
        {/* Marketplace buttons */}
        <div className="flex gap-1.5">
          <button onClick={() => setMarketplace('')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              !marketplace ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>All</button>
          {availableMarkets.map(id => (
            <button key={id} onClick={() => setMarketplace(id === marketplace ? '' : id)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                marketplace === id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>{MARKETPLACE_LABELS[id] ?? id}</button>
          ))}
        </div>
        {/* Product selector */}
        <select value={selectedGroup} onChange={e => { setSelectedGroup(e.target.value); setSelectedAsin(''); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All Products</option>
          {productGroups.map(g => (
            <option key={g.label} value={g.label}>{g.label} ({g.products.length})</option>
          ))}
        </select>
        {activeGroup && activeGroup.products.length > 1 && (
          <select value={selectedAsin} onChange={e => setSelectedAsin(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">All Variations</option>
            {activeGroup.products.map(p => {
              const variant = p.title.match(/\(([^)]+)\)$/)?.[1] ?? p.asin;
              return <option key={p.asin} value={p.asin}>{variant}</option>;
            })}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400">Loading...</div>
      ) : (
        <>
          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Card label="Total Returns" value={summary.total_returns.toLocaleString()} />
              <Card label="Return Rate" value={`${summary.return_rate}%`}
                sub={`${summary.total_returns.toLocaleString()} / ${summary.total_order_units.toLocaleString()} units`}
                alert={summary.return_rate > 10} />
              <Card label="Top Reason" value={summary.top_reasons[0]?.reason ?? 'N/A'}
                sub={summary.top_reasons[0] ? `${summary.top_reasons[0].qty} units` : ''} />
              <Card label="Entries" value={summary.total_entries.toLocaleString()} />
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200">
            <button onClick={() => setTab('insights')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'insights' ? 'border-brand-500 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>Return Insights</button>
            <button onClick={() => setTab('orders')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'orders' ? 'border-brand-500 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>Return Orders</button>
          </div>

          {tab === 'insights' && (
            <>
              {/* Insights row: reasons + disposition + per-product */}
              {summary && (
                <div className="grid gap-4 md:grid-cols-3">
              {/* Top Return Reasons */}
              {summary.top_reasons.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold text-gray-700">Top Return Reasons</h3>
                  <div className="space-y-2">
                    {summary.top_reasons.slice(0, 5).map(r => (
                      <div key={r.reason} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between text-xs text-gray-600">
                            <span className="truncate max-w-[180px]" title={r.reason}>{r.reason}</span>
                            <span className="font-medium">{r.qty} units</span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-gray-100">
                            <div className="h-1.5 rounded-full bg-red-400"
                              style={{ width: `${Math.min(100, (r.qty / summary.total_returns) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Disposition */}
              {summary.by_disposition.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold text-gray-700">By Disposition</h3>
                  <div className="space-y-2">
                    {summary.by_disposition.map(d => (
                      <div key={d.disposition} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between text-xs text-gray-600">
                            <span>{d.disposition || 'Unknown'}</span>
                            <span className="font-medium">{d.qty} units</span>
                          </div>
                          <div className="mt-1 h-1.5 rounded-full bg-gray-100">
                            <div className="h-1.5 rounded-full bg-amber-400"
                              style={{ width: `${Math.min(100, (d.qty / summary.total_returns) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-Product Return Rate */}
              {summary.by_product && summary.by_product.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold text-gray-700">Return Rate by Product</h3>
                  <div className="space-y-2">
                    {summary.by_product.slice(0, 5).map(p => {
                      const rate = p.order_units > 0 ? Math.round((p.return_units / p.order_units) * 1000) / 10 : 0;
                      return (
                        <div key={p.asin} className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-gray-600">
                              <span className="truncate max-w-[140px]" title={p.title}>{displayName(p.sku) || p.title?.slice(0, 25)}</span>
                              <span className={`font-medium ${rate > 10 ? 'text-red-600' : rate > 5 ? 'text-amber-600' : 'text-gray-600'}`}>
                                {rate}% ({p.return_units}/{p.order_units})
                              </span>
                            </div>
                            <div className="mt-1 h-1.5 rounded-full bg-gray-100">
                              <div className={`h-1.5 rounded-full ${rate > 10 ? 'bg-red-500' : rate > 5 ? 'bg-amber-400' : 'bg-green-400'}`}
                                style={{ width: `${Math.min(100, rate * 3)}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

              {/* Monthly Trend */}
              {summary && summary.by_month && summary.by_month.length > 1 && (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Monthly Trend</h3>
                  <div className="flex items-end gap-2 h-24">
                    {summary.by_month.map(m => {
                      const maxQty = Math.max(...(summary.by_month ?? []).map(x => x.qty));
                      const h = maxQty > 0 ? (m.qty / maxQty * 100) : 0;
                      return (
                        <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[10px] text-gray-500">{m.qty}</span>
                          <div className="w-full flex flex-col rounded-t overflow-hidden" style={{ height: `${h}%`, minHeight: m.qty > 0 ? '4px' : '0' }}>
                            {m.unsellable > 0 && <div className="bg-red-400" style={{ flex: m.unsellable }} />}
                            {m.sellable > 0 && <div className="bg-green-400" style={{ flex: m.sellable }} />}
                            {m.sellable === 0 && m.unsellable === 0 && <div className="bg-gray-300 flex-1" />}
                          </div>
                          <span className="text-[10px] text-gray-400">{m.month.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-4 mt-2 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />Sellable</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />Unsellable</span>
                  </div>
                </div>
              )}

              {/* Per-SKU Sellable vs Unsellable */}
              {summary && summary.by_product && summary.by_product.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Sellable vs Unsellable by SKU</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-gray-100">
                        <tr>
                          <th className="pb-2 text-left text-xs font-medium text-gray-500">Product</th>
                          <th className="pb-2 text-right text-xs font-medium text-gray-500">Returns</th>
                          <th className="pb-2 text-right text-xs font-medium text-gray-500">Sellable</th>
                          <th className="pb-2 text-right text-xs font-medium text-gray-500">Unsellable</th>
                          <th className="pb-2 text-right text-xs font-medium text-gray-500">Loss %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {summary.by_product.map(s => {
                          const lossPct = s.return_units > 0 ? (s.unsellable / s.return_units * 100) : 0;
                          return (
                            <tr key={s.sku} className="hover:bg-gray-50">
                              <td className="py-2 font-medium text-gray-800" title={s.sku}>
                                {parentNickname(s.sku) && <span className="text-[10px] text-gray-400 block font-normal">{parentNickname(s.sku)}</span>}
                                <span>{displayName(s.sku)}</span>
                              </td>
                              <td className="py-2 text-right text-gray-700 font-medium">{s.return_units}</td>
                              <td className="py-2 text-right text-green-600">{s.sellable}</td>
                              <td className="py-2 text-right text-red-600">{s.unsellable}</td>
                              <td className={`py-2 text-right text-xs font-medium ${lossPct > 50 ? 'text-red-600' : lossPct > 25 ? 'text-yellow-600' : 'text-green-600'}`}>
                                {lossPct.toFixed(0)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Per-product return cards (ScaleInsights style) */}
              {returns.length > 0 && <ProductReturnCards returns={returns} displayName={displayName} parentNickname={parentNickname} />}
            </>
          )}

          {tab === 'orders' && (
            <>
          {/* Returns table */}
          {returns.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-gray-400">No returns for this period</div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Order ID</th>
                    <th className="px-4 py-3">Market</th>
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3 text-right">Qty</th>
                    <th className="px-4 py-3">Reason</th>
                    <th className="px-4 py-3">Disposition</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {returns.map((r) => (
                    <Fragment key={r.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedRow(expandedRow === r.id ? null : r.id)}>
                        <td className="whitespace-nowrap px-4 py-2 text-gray-600">{r.date}</td>
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">
                          <a href={scOrderUrl(r.marketplace, r.order_id)} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-brand-600 hover:text-brand-800 hover:underline">{r.order_id}</a>
                        </td>
                        <td className="px-4 py-2 text-gray-600">{MARKETPLACE_LABELS[r.marketplace] ?? r.marketplace}</td>
                        <td className="px-4 py-2 text-xs" title={r.sku}>
                          {parentNickname(r.sku) && <span className="text-[10px] text-gray-400 block">{parentNickname(r.sku)}</span>}
                          <span>{displayName(r.sku)}</span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.quantity}</td>
                        <td className="max-w-[160px] truncate px-4 py-2 text-gray-600" title={r.reason}>{r.reason}</td>
                        <td className="px-4 py-2 text-gray-600">{r.disposition}</td>
                        <td className="px-4 py-2 text-gray-600">{r.status || <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            {r.notes?.length > 0 && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-600">
                                {r.notes?.length} {r.notes?.length === 1 ? 'note' : 'notes'}
                              </span>
                            )}
                            {r.notes?.some(n => n.action_type === 'resolved') && (
                              <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">Resolved</span>
                            )}
                            {r.notes?.some(n => n.action_type === 'refund_requested') && !r.notes?.some(n => n.action_type === 'resolved') && (
                              <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Refund</span>
                            )}
                            <button onClick={e => { e.stopPropagation(); setExpandedRow(expandedRow === r.id ? null : r.id); }}
                              className="ml-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" title="Add action">
                              +
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRow === r.id && (
                        <tr key={`${r.id}-expand`}>
                          <td colSpan={9} className="bg-gray-50 px-6 py-3">
                            <div className="space-y-3">
                              {/* Customer comments */}
                              {r.customer_comments && (
                                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs">
                                  <span className="font-medium text-amber-700">Customer comment: </span>
                                  <span className="text-amber-900">{r.customer_comments}</span>
                                </div>
                              )}

                              {/* Existing notes */}
                              {r.notes?.length > 0 && (
                                <div className="space-y-1.5">
                                  {r.notes?.map(n => (
                                    <div key={n.id} className="flex items-start gap-2 text-xs">
                                      <span className={`inline-block rounded-full px-2 py-0.5 font-medium ${ACTION_COLORS[n.action_type] ?? ACTION_COLORS.note}`}>
                                        {ACTION_TYPES.find(a => a.value === n.action_type)?.label ?? n.action_type}
                                      </span>
                                      <span className="flex-1 text-gray-700">{n.note}</span>
                                      <span className="text-gray-400 whitespace-nowrap">{n.created_at?.slice(0, 16)}</span>
                                      <button onClick={() => deleteNote(r.id, n.id)}
                                        className="text-gray-300 hover:text-red-500" title="Delete">x</button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Add new note/action */}
                              <div className="flex items-center gap-2">
                                <select value={noteAction} onChange={e => setNoteAction(e.target.value)}
                                  className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs">
                                  {ACTION_TYPES.map(a => (
                                    <option key={a.value} value={a.value}>{a.icon} {a.label}</option>
                                  ))}
                                </select>
                                <input type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                                  placeholder="Add a note..."
                                  onKeyDown={e => { if (e.key === 'Enter') addNote(r.id); }}
                                  className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs" />
                                <button onClick={() => addNote(r.id)}
                                  disabled={!noteText.trim()}
                                  className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-40">
                                  Add
                                </button>
                              </div>

                              {/* Quick details */}
                              <div className="flex gap-4 text-xs text-gray-500">
                                <span>FC: {r.fulfillment_center || 'N/A'}</span>
                                <span>ASIN: {r.asin}</span>
                                <span>SKU: {r.sku}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ── Per-product return insight cards (ScaleInsights style) ─── */
function ProductReturnCards({ returns, displayName, parentNickname }: { returns: ReturnRow[]; displayName: (s: string) => string; parentNickname: (s: string) => string | null }) {
  const [expandedAsin, setExpandedAsin] = useState<string | null>(null);

  // Group returns by ASIN
  const byProduct = new Map<string, { asin: string; sku: string; title: string; returns: ReturnRow[] }>();
  for (const r of returns) {
    if (!byProduct.has(r.asin)) byProduct.set(r.asin, { asin: r.asin, sku: r.sku, title: r.title, returns: [] });
    byProduct.get(r.asin)!.returns.push(r);
  }
  const products = [...byProduct.values()].sort((a, b) => b.returns.length - a.returns.length);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700">{products.length} products with returns</h3>
      {products.map(p => {
        const totalQty = p.returns.reduce((s, r) => s + r.quantity, 0);
        const reasons = new Map<string, number>();
        const dispositions = new Map<string, number>();
        for (const r of p.returns) {
          reasons.set(r.reason, (reasons.get(r.reason) ?? 0) + r.quantity);
          dispositions.set(r.disposition, (dispositions.get(r.disposition) ?? 0) + r.quantity);
        }
        const isExpanded = expandedAsin === p.asin;

        return (
          <div key={p.asin} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {/* Product header */}
            <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedAsin(isExpanded ? null : p.asin)}>
              <div className="flex-1 min-w-0">
                <div>
                  {parentNickname(p.sku) && <span className="text-[10px] text-gray-400 block">{parentNickname(p.sku)}</span>}
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900">{displayName(p.sku)}</span>
                    <span className="text-xs text-gray-400 font-mono">{p.asin}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm shrink-0">
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">{totalQty}</div>
                  <div className="text-[10px] uppercase text-gray-400">units</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-700">{p.returns.length}</div>
                  <div className="text-[10px] uppercase text-gray-400">returns</div>
                </div>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div className="border-t border-gray-100 px-5 py-4 space-y-4 bg-gray-50/50">
                {/* Reason + disposition summary */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Reasons</h4>
                    <div className="space-y-1">
                      {[...reasons.entries()].sort((a, b) => b[1] - a[1]).map(([reason, qty]) => (
                        <div key={reason} className="flex justify-between text-xs">
                          <span className="text-gray-600">{reason}</span>
                          <span className="font-medium text-gray-800">{qty}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Disposition</h4>
                    <div className="space-y-1">
                      {[...dispositions.entries()].sort((a, b) => b[1] - a[1]).map(([disp, qty]) => (
                        <div key={disp} className="flex justify-between text-xs">
                          <span className="text-gray-600">{disp}</span>
                          <span className={`font-medium ${disp === 'SELLABLE' ? 'text-green-600' : disp === 'DEFECTIVE' ? 'text-red-600' : 'text-amber-600'}`}>{qty}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Individual return entries */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Return Orders</h4>
                  <div className="space-y-2">
                    {p.returns.map((r, i) => (
                      <div key={`${r.order_id}-${i}`} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                          <span className="text-gray-500">{r.date}</span>
                          <a href={scOrderUrl(r.marketplace, r.order_id)} target="_blank" rel="noopener noreferrer"
                            className="font-mono text-brand-600 hover:underline">{r.order_id}</a>
                          <span className="text-gray-500">{MARKETPLACE_LABELS[r.marketplace] ?? r.marketplace}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            r.disposition === 'SELLABLE' ? 'bg-green-100 text-green-700'
                            : r.disposition === 'DEFECTIVE' ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                          }`}>{r.disposition}</span>
                          <span className="text-gray-600">{r.reason}</span>
                          <span className="text-gray-400">Qty: {r.quantity}</span>
                          {r.status && <span className="text-gray-400">{r.status}</span>}
                        </div>
                        {r.customer_comments && (
                          <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                            "{r.customer_comments}"
                          </p>
                        )}
                        {r.notes && r.notes.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {r.notes.map(n => (
                              <span key={n.id} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${ACTION_COLORS[n.action_type] ?? ACTION_COLORS.note}`}>
                                {ACTION_TYPES.find(a => a.value === n.action_type)?.label ?? n.action_type}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Card({ label, value, sub, alert }: { label: string; value: string; sub?: string; alert?: boolean }) {
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${alert ? 'border-red-300' : 'border-gray-200'}`}>
      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${alert ? 'text-red-600' : ''}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </div>
  );
}
